<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { usePostHog } from '@/composables/usePostHog';

@Component
export default class InfraLayerCard extends Vue {
  @Prop({ required: true }) private layerNumber!: string;
  @Prop({ required: true }) private title!: string;
  @Prop({ required: true }) private description!: string;
  @Prop({ required: true }) private badge!: string;
  @Prop({ required: true }) private icon!: string;
  @Prop({ default: 'ln-glass-teal' }) private variant!: string;

  private posthog = usePostHog().posthog;

  private handleClick() {
    this.posthog.capture('infra_layer_clicked', {
      layer_number: this.layerNumber,
      layer_title: this.title,
      layer_badge: this.badge
    });
    this.$emit('click');
  }
}
</script>

<template>
  <div :class="['layer-step-card', variant]" @click="handleClick">
    <div class="layer-badge">{{ badge }}</div>
    <div class="icon-circle"><span :class="['mdi', icon]"></span></div>
    <div class="layer-info">
      <div class="layer-number">{{ layerNumber }}</div>
      <h3>{{ title }}</h3>
      <p>{{ description }}</p>
    </div>
    <div class="explore-link">Explorer <span class="mdi mdi-arrow-right"></span></div>
  </div>
</template>

<style scoped>
.layer-step-card {
  display: flex;
  flex-direction: column;
  padding: 3rem 2rem;
  border-radius: var(--ln-radius-lg);
  cursor: pointer;
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  background: var(--ln-glass-base);
  border: 1px solid var(--ln-glass-border);
  backdrop-filter: blur(var(--ln-glass-blur));
  position: relative;
  min-height: 500px;
  width: 100%;
  box-sizing: border-box;
}

.layer-step-card:hover {
  transform: translateY(-12px);
  box-shadow: 0 20px 40px var(--ln-glass-shadow-elevated);
  border-color: var(--ln-teal-deep);
}

.icon-circle {
  width: 60px;
  height: 60px;
  background: var(--ln-glass-surface);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  border: 1px solid var(--ln-glass-border);
}

.icon-circle span.mdi {
  font-size: 2rem;
  color: var(--ln-teal-deep);
}

.layer-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.3rem 0.7rem;
  background: var(--ln-glass-surface);
  border-radius: 2rem;
  color: var(--ln-teal-deep);
}

.layer-number {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 1.5px;
  color: var(--ln-stone-grey);
  margin-bottom: 0.5rem;
}

.layer-step-card h3 {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--ln-text-primary);
  line-height: 1.2;
  margin-bottom: 1.2rem;
}

.layer-step-card p {
  font-size: 1.1rem;
  color: var(--ln-text-muted);
  line-height: 1.6;
}

.explore-link {
  margin-top: auto;
  padding: 0.8rem 0;
  background: transparent;
  color: var(--ln-teal-deep);
  border-top: 1px solid var(--ln-glass-border);
  font-weight: 800;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

.layer-step-card:hover .explore-link {
  color: var(--ln-teal-light);
  border-top-color: var(--ln-teal-deep);
  padding-left: 0.5rem;
}

.layer-step-card:hover .mdi-arrow-right {
  transform: translateX(8px);
}
</style>
